/**
 * SmartRuleSection — the "make this collection smart" UI
 * (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §4.2).
 *
 * A standalone rule editor, not the item list's QueryStatementBar: that bar
 * reads the item list's full field catalog, and a smart collection's rule is
 * held to the narrower one below. (The bar no longer needs the item list's
 * page — useStatementQuery takes filters and sort as arguments and leaves the
 * URL alone with syncUrl off, which is how the Report Designer mounts it.)
 * Same UX shape though: mono input, live parse, plain-English strip, errors
 * with position. Vocabulary is the curated SMART_COLLECTION_FIELDS catalog
 * (see smartCollectionFields.ts for why it's narrower than the item list's).
 */

import { useMemo, useState } from 'react';
import { AlertCircle, Sparkles, X } from 'lucide-react';
import {
  buildCatalog,
  printQuery,
  safeParse,
  toEnglish,
} from '../../../lib/pql';
import { SMART_COLLECTION_FIELDS } from '../../../lib/collections/smartCollectionFields';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';
import { useSavedReportsStore } from '../../../stores/prioritization/savedReportsStore';
import { getReportOrigin } from '../../../lib/reportDependents';
import type { Collection } from '../../../types/prioritization/collections';

const catalog = buildCatalog(SMART_COLLECTION_FIELDS);

interface SmartRuleSectionProps {
  collection: Collection;
  actorId?: string;
  matchCount: number;
}

export function SmartRuleSection({ collection, actorId, matchCount }: SmartRuleSectionProps) {
  const bindRule = useCollectionsStore((s) => s.bindRule);
  const unbindRule = useCollectionsStore((s) => s.unbindRule);
  const boundReport = useSavedReportsStore((s) =>
    collection.ruleQueryId ? s.getReportById(collection.ruleQueryId) : undefined,
  );

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const parseResult = useMemo(() => safeParse(draft, catalog), [draft]);
  const englishPreview = useMemo(
    () => (parseResult.ok ? toEnglish(parseResult.query, catalog) : null),
    [parseResult],
  );

  const isSmart = Boolean(collection.ruleQueryId) && boundReport?.queryAst !== undefined;
  // This rule is a copy of whatever query it was made from; the two diverge.
  // Say so here rather than let someone edit the origin and expect this to move.
  const origin = collection.ruleQueryId ? getReportOrigin(collection.ruleQueryId) : null;

  const startEditing = () => {
    setDraft(boundReport?.queryText ?? '');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!parseResult.ok) return;
    setSaving(true);
    try {
      const canonical = printQuery(parseResult.query);
      const ok = await bindRule(collection.id, canonical, parseResult.query.where, actorId ?? 'system', origin?.id ?? null);
      if (ok) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleUnbind = async () => {
    await unbindRule(collection.id);
    setEditing(false);
  };

  if (!isSmart && !editing) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-[var(--text-sec)]">Not a Smart Collection</p>
          <p className="text-[11px] text-[var(--text-ter)] mt-0.5">
            Bind a rule so this collection auto-updates with new matches — you can still pin or exclude specific items.
          </p>
        </div>
        <button
          type="button"
          onClick={startEditing}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Make Smart
        </button>
      </div>
    );
  }

  if (isSmart && !editing) {
    return (
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
              <span className="text-xs font-semibold text-[var(--text-sec)]">Smart Collection</span>
              <span className="text-[11px] text-[var(--text-sec)] tabular-nums">· {matchCount} live match{matchCount === 1 ? '' : 'es'}</span>
            </div>
            <code className="block mt-1 text-[11px] font-mono text-[var(--text-sec)] truncate">
              {boundReport?.queryText ?? ''}
            </code>
            {origin && (
              <p className="mt-1 text-[10px] text-[var(--text-sec)]">
                Copied from the saved query “{origin.name}”. This rule is its own — editing that query does not change it.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={startEditing}
              className="text-[11px] font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            >
              Edit rule
            </button>
            <button
              type="button"
              onClick={() => void handleUnbind()}
              className="text-[11px] font-medium text-[var(--text-sec)] hover:text-[var(--text-sec)] transition-colors"
              title="Revert to a static collection — pinned/excluded overrides are kept as explicit membership"
            >
              Unbind
            </button>
          </div>
        </div>
      </div>
    );
  }

  // editing === true
  return (
    <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave();
            if (e.key === 'Escape') setEditing(false);
          }}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          placeholder='e.g. itemCategory = risk AND exit_status = active AND updatedAt <= -30d'
          className={`flex-1 font-mono text-[13px]  border px-3 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 ${
            !parseResult.ok && draft
              ? 'border-[var(--coral)] focus:border-[var(--coral)] focus:ring-red-100'
              : 'border-[var(--slate)] focus:border-[var(--slate)] focus:ring-violet-100'
          }`}
        />
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!parseResult.ok || saving || !draft.trim()}
          className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="shrink-0 p-1.5 text-[var(--text-sec)] hover:text-[var(--text-sec)] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-1.5 pl-6 text-[11px] leading-4">
        {!parseResult.ok && draft ? (
          <span className="flex items-start gap-1 text-[var(--coral)]">
            <AlertCircle className="mt-px h-3 w-3 shrink-0" />
            {parseResult.error.message}
          </span>
        ) : englishPreview ? (
          <span className="italic text-[var(--text-sec)]">{englishPreview}</span>
        ) : (
          <span className="text-[var(--text-sec)]">
            Fields: category, status, altitude, tags, source, title, description, created/updated dates, dedupe status, custom fields (meta.key)
          </span>
        )}
      </div>
    </div>
  );
}
